import React, { useEffect, useState } from 'react';
import gql from 'graphql-tag';
import { useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Skeleton } from '@material-ui/lab';
import { Divider } from '@material-ui/core';

import { Loading } from 'core/components/loading';
import { Button } from 'core/components/button';
import Label from 'core/components/label/label';
import Section from 'core/components/section';
import { useToast } from 'core/components/toast';
import LabelWithCopyButton from 'core/components/label-with-copy-button';
import { track } from 'core/helpers/analytics';
import { dateFormatter } from 'core/helpers/formatters';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { buildBranchPlusQuoteFromPolicy, CarrierData, getCarrierCommissions } from './helpers';
import { IndividualBranchPlusOffer } from './individual-branch-plus-offer';
import AlertIconWhiteFilled from 'core/assets/svg/alert-white-filled.svg';
import infoIcon from 'core/assets/svg/ic-info-white.svg';
import useStyles from './branch-plus.styles';

const generateTimestamp = (date) => {
  const calendarDate = dateFormatter(date);
  const newDate = new Date(date);
  const hours = newDate.getHours();
  const minutes = newDate.getMinutes();
  if (hours > 12) {
    return `Offers generated on ${calendarDate} @ ${hours - 12}:${minutes} PM`;
  }
  return `Offers generated on ${calendarDate} @ ${hours}:${minutes} AM`;
};

const filterAndSort = (offers, policyType) => {
  return offers
    .filter((offer) => offer.policyType === policyType)
    .sort((a, b) => {
      if (a.status === 'success' && b.status === 'success') {
        // lowest monthly should show up first
        if (a.monthly <= b.monthly) {
          return -1;
        }
        return 1;
      }
      // success should show up before errors
      if (a.status === 'success') {
        return -1;
      }
      return 1;
    });
};

const GET_RECENT_BRANCH_PLUS_OFFERS = gql`
  query getOffer($offerId: ID!) {
    offer: getOffer(id: $offerId) {
      branchPlusOffers {
        id
        bridgeUrl
        carrierName
        carrierId
        monthly
        policyType
        premium
        provider
        status
        message
        sessionId
      }
    }
  }
`;

export const BranchPlus = observer(({ policyType, fromPolicy }) => {
  const classes = useStyles();
  const session = useSession();
  const store = useStore();
  const history = useHistory();
  const toast = useToast();
  const [offerInput, setOfferInput] = useState(false);
  const [branchPlusLoading, setBranchPlusLoading] = useState(false);

  const [getRecentBranchPlusOffers] = useLazyQuery(GET_RECENT_BRANCH_PLUS_OFFERS);

  const {
    quote: { generateBranchPlusOffersFromOfferAction, loading, cleanup },
    offer: { offer, getOffer },
    account: {
      fname,
      lname,
      policies: {
        policy: policyStore,
        policy: { branchPlusOffers, updateBranchPlusOffers, policy }
      },
      policies
    }
  } = store;

  const [branchPlusLinkedOfferId, setBranchPlusLinkedOfferId] = useState(
    fromPolicy
      ? policy?.branchPlusLinkedOfferId
      : offer.branchPlusOffers?.find((bpo) => bpo.policyType === policyType)
        ? offer.id
        : null
  );

  const webUserId = offerInput.webUserId;
  const policyId = fromPolicy ? offerInput.id : undefined;
  const offerId = fromPolicy ? undefined : offerInput.id;
  const rep = session.user;
  const componentLoading = loading || branchPlusLoading;
  const isBranchPlusOnly = !fromPolicy && store.offer.isBranchPlusOnly;

  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  useEffect(() => {
    setOfferInput(
      fromPolicy
        ? buildBranchPlusQuoteFromPolicy({
            list: policies.list,
            fname,
            lname,
            policy: policy
          })
        : offer
    );
  }, [fname, fromPolicy, lname, offer, policy, policies.list]);
  const state = fromPolicy ? policyStore.state : offer.quote.correctedAddress.state;

  const sortedBranchPlusOffers = fromPolicy
    ? filterAndSort([...(branchPlusOffers || [])], policyType)
    : filterAndSort([...(offer.branchPlusOffers || [])], policyType);

  const handleGenerateExternalQuotes = async () => {
    setBranchPlusLoading(true);

    try {
      // if we have previously generated quotes for this policy, we can fetch them instead of generating new ones
      if (fromPolicy && policy.branchPlusLinkedOfferId && sortedBranchPlusOffers.length === 0) {
        const {
          data: {
            offer: { branchPlusOffers }
          },
          error
        } = await getRecentBranchPlusOffers({
          variables: {
            offerId: policy.branchPlusLinkedOfferId
          }
        });
        if (error) {
          throw new Error(error);
        }
        if (branchPlusOffers?.length) {
          updateBranchPlusOffers(branchPlusOffers);
        }
        // otherwise we need to generate new quotes
      } else {
        track('Generate Branch Plus Quotes from Staff', {
          webUserId,
          policyId,
          offerId,
          policyType,
          rep: rep.username
        });

        const branchPlusResponse = await generateBranchPlusOffersFromOfferAction({
          quoteDetails: offerInput.quote,
          webUserId,
          rep,
          policyId,
          offerId,
          policyType
        });

        const validResponse = branchPlusResponse?.offerId && branchPlusResponse?.branchPlusOffers?.length > 0;

        if (validResponse) {
          setBranchPlusLinkedOfferId(branchPlusResponse.offerId);

          if (fromPolicy) {
            // for policy page we need to update the branchPlusOffers node in the policy store
            updateBranchPlusOffers(branchPlusResponse.branchPlusOffers);
          } else {
            // for the offer page we need to update our route, and then fetch the new offer
            history.push(`/offer/${branchPlusResponse.offerId}`);
            getOffer(branchPlusResponse.offerId);
          }
        }
      }
    } catch (error) {
      toast.notify({
        message: error.message,
        type: 'error'
      });
    } finally {
      setBranchPlusLoading(false);
    }
  };

  if (isBranchPlusOnly && sortedBranchPlusOffers.length === 0) {
    return null;
  }

  let buttonText = 'Generate external quotes';
  if (fromPolicy && policy.branchPlusLinkedOfferId && sortedBranchPlusOffers.length === 0) {
    buttonText = 'Retrieve recent external quotes';
  }
  if (sortedBranchPlusOffers.length > 0) {
    buttonText = 'Regenerate external quotes';
  }

  return (
    <Section
      title={`Branch Plus ${fromPolicy ? '' : `${policyType === 'A' ? 'Auto ' : 'Home '} `}Offers`}
      titleTooltipText="When you enable Branch Plus offers, you will receive quotes from our external carrier partners based on the coverage selections you have made for this policy. It's important to note that the quotes generated are preliminary and may not reflect the final price. They are subject to change before the purchase is made, and you will need to manage them separately."
      type="SubSection"
      id="BranchPlusOffers"
    >
      <div className={classes.container}>
        {isBranchPlusOnly ? (
          <>
            <div className={classes.header}>
              <div>{generateTimestamp(offer.initialCreationDateTime)}</div>
              <div className={classes.regenerateSection}>
                <img src={infoIcon} className={classes.infoIcon} alt="info Icon" />
                To regenerate, please create a new offer.
              </div>
            </div>
            {sortedBranchPlusOffers.map((branchPlusOffer, index) => (
              <IndividualBranchPlusOffer
                key={`${branchPlusOffer.carrierId}-${index}`}
                branchPlusOffer={branchPlusOffer}
                carrierData={CarrierData[branchPlusOffer.carrierId]}
                policyType={policyType}
                webUserId={webUserId}
                policyId={policyId}
                offerId={offerId}
                repUsername={rep.username}
                commissions={getCarrierCommissions(branchPlusOffer.carrierId, state, policyType)}
              />
            ))}
          </>
        ) : (
          <>
            <div className={classes.content}>
              <Button
                variant="contained"
                color="secondary"
                className={classes.button}
                disabled={componentLoading}
                onClick={handleGenerateExternalQuotes}
              >
                {buttonText}
              </Button>
              {branchPlusLinkedOfferId && (
                <div className={classes.linkedOfferContainer}>
                  <Label type="coverageLabel">Branch Plus Linked Offer ID</Label>
                  <LabelWithCopyButton type="smallSubtitleLight">{branchPlusLinkedOfferId}</LabelWithCopyButton>
                </div>
              )}
            </div>
            {componentLoading
              ? Object.values(CarrierData).map((_, index) => (
                  <div className={classes.loadingContainer} key={index}>
                    <Skeleton width="100%" animation="wave" height={56} className={classes.skeleton} />
                    <Divider className={classes.divider} />
                    <div className={classes.loadingContent}>
                      <Loading type="secondary" noBackground />
                    </div>
                  </div>
                ))
              : sortedBranchPlusOffers.map((branchPlusOffer, index) => (
                  <IndividualBranchPlusOffer
                    key={`${branchPlusOffer.carrierId}-${index}`}
                    branchPlusOffer={branchPlusOffer}
                    carrierData={CarrierData[branchPlusOffer.carrierId]}
                    policyType={policyType}
                    webUserId={webUserId}
                    policyId={policyId}
                    offerId={offerId}
                    repUsername={rep.username}
                    commissions={getCarrierCommissions(branchPlusOffer.carrierId, state, policyType)}
                  />
                ))}
          </>
        )}
        <div className={classes.outlineContainer}>
          <img src={AlertIconWhiteFilled} className={classes.icon} alt="Banner Icon" />
          <Label>
            These coverages are purchased through external carriers, and are handled separately. The quotes generated
            may not reflect the final price and are subject to change prior to purchase.
          </Label>
        </div>
      </div>
    </Section>
  );
});

BranchPlus.propTypes = {
  policyType: PropTypes.string.isRequired,
  fromPolicy: PropTypes.bool
};

BranchPlus.defaultProps = {
  fromPolicy: false
};
